import React, { useMemo, useState } from 'react';
import cl from './Calculator.module.css';
import {
    IconStudent, IconRobot, IconStar, IconArrowRight,
    IconTeacher, IconDashboard, IconBook,
} from '../../components/icons';
import useReveal from '../../hooks/useReveal';

// Калькулятор окупаемости подписки СтартКласс.
// Лид настраивает доходы (число учеников, цена абонемента) и расходы
// (зарплата преподавателю, аренда, прочее) — видит выручку и прибыль за год.
//
// Все дефолты лежат в одном месте — _DEFAULTS. Чтобы поменять стартовые
// значения формы и текст подписей под полями, правим только тут.

const _DEFAULTS = {
    // Доходы
    studentsProg:  50,    // учеников на программировании
    studentsRobo:  50,    // учеников на робототехнике
    price:         4000,  // месячный абонемент
    // Расходы
    teacherCost:   25000, // зарплата преподавателя / мес
    rentCost:      15000, // аренда помещения / мес
    otherCost:     5000,  // прочие расходы / мес (расходники, связь, маркетинг)
    // Константы
    months:        9,     // 9 учебных месяцев
    subscription:  150000, // годовой Стандарт
};

// Форматер чисел с разделителями тысяч: 250000 → "250 000 ₽"
function fmt(n) {
    return new Intl.NumberFormat('ru-RU').format(Math.round(n)) + ' ₽';
}


export default function Calculator() {
    // Доходы
    const [studentsProg, setStudentsProg] = useState(_DEFAULTS.studentsProg);
    const [studentsRobo, setStudentsRobo] = useState(_DEFAULTS.studentsRobo);
    const [price,        setPrice]        = useState(_DEFAULTS.price);
    // Расходы
    const [teacherCost,  setTeacherCost]  = useState(_DEFAULTS.teacherCost);
    const [rentCost,     setRentCost]     = useState(_DEFAULTS.rentCost);
    const [otherCost,    setOtherCost]    = useState(_DEFAULTS.otherCost);

    const calc = useMemo(() => {
        const totalStudents   = (+studentsProg || 0) + (+studentsRobo || 0);
        const monthlyRevenue  = totalStudents * (+price || 0);
        const yearlyRevenue   = monthlyRevenue * _DEFAULTS.months;

        const monthlyExpenses = (+teacherCost || 0) + (+rentCost || 0) + (+otherCost || 0);
        const yearlyExpenses  = monthlyExpenses * _DEFAULTS.months;
        const yearlyTotalCost = yearlyExpenses + _DEFAULTS.subscription;

        const yearlyProfit    = yearlyRevenue - yearlyTotalCost;

        // Срок окупаемости подписки в месяцах от чистой месячной прибыли
        // (выручка минус операционные расходы, без подписки).
        const monthlyOperatingProfit = monthlyRevenue - monthlyExpenses;
        const breakevenMonths = monthlyOperatingProfit > 0
            ? _DEFAULTS.subscription / monthlyOperatingProfit
            : 0;

        return {
            totalStudents,
            monthlyRevenue,
            yearlyRevenue,
            monthlyExpenses,
            yearlyExpenses,
            yearlyTotalCost,
            yearlyProfit,
            breakevenMonths,
        };
    }, [studentsProg, studentsRobo, price, teacherCost, rentCost, otherCost]);

    const revealRef = useReveal();
    return (
        <section className={cl.calc} id="calculator" ref={revealRef}>
            <div className={cl.inner}>
                <div className={cl.head}>
                    <span className={cl.eyebrow} data-reveal="down">Окупаемость</span>
                    <h2 className={cl.title} data-reveal="up" style={{ '--reveal-delay': '100ms' }}>
                        Посчитайте, сколько <span className={cl.accent}>вы заработаете</span> за год
                    </h2>
                    <p className={cl.subtitle} data-reveal="up" style={{ '--reveal-delay': '180ms' }}>
                        Введите параметры вашего центра — увидите выручку,
                        прибыль и срок окупаемости подписки. Расходы тоже настраиваются
                        ползунками, чтобы цифры были честные под ваш центр.
                    </p>
                </div>

                <div className={cl.grid}>
                    {/* === ЛЕВАЯ ЧАСТЬ — ВВОДЫ === */}
                    <div className={cl.card} data-reveal="left">
                        <div className={cl.section}>
                            <div className={cl.sectionTitle}>
                                <span className={`${cl.sectionDot} ${cl.dotIn}`}/>
                                Доходы
                            </div>

                            <Field
                                icon={<IconStudent width={20} height={20}/>}
                                label="Учеников на программировании"
                                value={studentsProg}
                                onChange={setStudentsProg}
                                min={0} max={200} step={1}
                            />
                            <Field
                                icon={<IconRobot width={20} height={20}/>}
                                label="Учеников на робототехнике"
                                value={studentsRobo}
                                onChange={setStudentsRobo}
                                min={0} max={200} step={1}
                            />
                            <Field
                                icon={<IconStar width={20} height={20}/>}
                                label="Цена абонемента (₽/мес)"
                                value={price}
                                onChange={setPrice}
                                min={0} max={10000} step={100}
                                suffix="₽"
                            />
                        </div>

                        <div className={cl.section}>
                            <div className={cl.sectionTitle}>
                                <span className={`${cl.sectionDot} ${cl.dotOut}`}/>
                                Расходы центра в месяц
                            </div>

                            <Field
                                icon={<IconTeacher width={20} height={20}/>}
                                label="Зарплата преподавателю"
                                value={teacherCost}
                                onChange={setTeacherCost}
                                min={0} max={100000} step={1000}
                                suffix="₽"
                            />
                            <Field
                                icon={<IconDashboard width={20} height={20}/>}
                                label="Аренда помещения"
                                value={rentCost}
                                onChange={setRentCost}
                                min={0} max={100000} step={1000}
                                suffix="₽"
                            />
                            <Field
                                icon={<IconBook width={20} height={20}/>}
                                label="Прочие расходы (связь, расходники, маркетинг)"
                                value={otherCost}
                                onChange={setOtherCost}
                                min={0} max={100000} step={1000}
                                suffix="₽"
                            />
                        </div>

                        <div className={cl.assumptions}>
                            <b>В расчёте учтено:</b>
                            <ul>
                                <li>Учебный год — 9 месяцев</li>
                                <li>Подписка СтартКласс — 150 000 ₽/год (Стандарт)</li>
                                <li>Закуп оборудования <b>не учтён</b> — это разовые расходы</li>
                            </ul>
                        </div>
                    </div>

                    {/* === ПРАВАЯ ЧАСТЬ — РЕЗУЛЬТАТ === */}
                    <div className={cl.result} data-reveal="right" style={{ '--reveal-delay': '150ms' }}>
                        <ResultRow
                            label="Учеников всего"
                            value={`${calc.totalStudents}`}
                            sub="программирование + робототехника"
                        />
                        <ResultRow
                            label="Выручка в месяц"
                            value={fmt(calc.monthlyRevenue)}
                            sub={`${calc.totalStudents} × ${fmt(price).replace(' ₽','')} ₽`}
                        />
                        <ResultRow
                            label="Выручка за учебный год"
                            value={fmt(calc.yearlyRevenue)}
                            sub="за 9 месяцев"
                            highlight
                        />
                        <ResultRow
                            label="Расходы центра за год"
                            value={`− ${fmt(calc.yearlyExpenses)}`}
                            sub={`${fmt(calc.monthlyExpenses)}/мес × 9 месяцев`}
                            isCost
                        />
                        <ResultRow
                            label="Подписка СтартКласс"
                            value={`− ${fmt(_DEFAULTS.subscription)}`}
                            sub="годовой Стандарт-тариф"
                            isCost
                        />

                        <div className={cl.profit}>
                            <span className={cl.profitLabel}>Чистая прибыль за год</span>
                            <span className={cl.profitValue}>
                                {fmt(calc.yearlyProfit)}
                            </span>
                            {calc.breakevenMonths > 0 && (
                                <span className={cl.profitSub}>
                                    Подписка окупается за <b>{calc.breakevenMonths.toFixed(1)}</b> {monthsLabel(calc.breakevenMonths)}
                                </span>
                            )}
                        </div>

                        <a href="#tariff" className={cl.cta}>
                            Заказать счёт <IconArrowRight width={16} height={16}/>
                        </a>
                    </div>
                </div>

                <p className={cl.disclaimer}>
                    Расчёт ориентировочный. Реальные показатели зависят от посещаемости,
                    структуры расходов и маркетинга вашего центра.
                </p>
            </div>
        </section>
    );
}


// Поле с лейблом, ползунком и числовым вводом — синхронизированы.
function Field({ icon, label, value, onChange, min, max, step, suffix }) {
    return (
        <div className={cl.field}>
            <div className={cl.fieldHead}>
                <span className={cl.fieldIcon}>{icon}</span>
                <label className={cl.fieldLabel}>{label}</label>
            </div>
            <div className={cl.fieldControls}>
                <input
                    type="range"
                    min={min} max={max} step={step}
                    value={value}
                    onChange={e => onChange(+e.target.value)}
                    className={cl.range}
                />
                <input
                    type="number"
                    min={min} max={max} step={step}
                    value={value}
                    onChange={e => onChange(+e.target.value || 0)}
                    className={cl.number}
                />
                {suffix && <span className={cl.suffix}>{suffix}</span>}
            </div>
        </div>
    );
}


// Строка результата (метрика + подпись)
function ResultRow({ label, value, sub, highlight, isCost }) {
    const cls = [
        cl.row,
        highlight && cl.rowHi,
        isCost && cl.rowCost,
    ].filter(Boolean).join(' ');
    return (
        <div className={cls}>
            <div className={cl.rowLabel}>{label}</div>
            <div className={cl.rowValue}>{value}</div>
            {sub && <div className={cl.rowSub}>{sub}</div>}
        </div>
    );
}


// Склонение «месяц / месяца / месяцев» для подписи окупаемости.
// Подписи всегда в форме "X.Y", поэтому используем форму родительного падежа («месяца»).
// Для целых значений (1, 2, 5...) применяем стандартное правило русской грамматики.
function monthsLabel(n) {
    // Если дробное — всегда «месяца» (например, 1.3 месяца, 0.7 месяца).
    if (Math.abs(n) % 1 !== 0) return 'месяца';
    const v = Math.abs(n) % 100;
    if (v >= 11 && v <= 14) return 'месяцев';
    const last = v % 10;
    if (last === 1) return 'месяц';
    if (last >= 2 && last <= 4) return 'месяца';
    return 'месяцев';
}
